import { useCallback, useEffect, useState } from "react";

import { useSession } from "../../session/SessionContext.js";
import { AssistantIdentity } from "./AssistantIdentity.js";

/**
 * The prompt builder.
 *
 * What an administrator can change here: the assistant's name, how it opens, the tone
 * it takes, and the exact words it uses when it declines.
 *
 * What they cannot change — and this is worth stating on the screen itself, not only
 * in the code — is any of the rules. The medical refusal, the competitor rule, the
 * prompt-extraction defence, the confidence floor and the grounding check all live in
 * `@island/ai-core` and are enforced in code before and after the model runs. Tone
 * instructions are appended to the system prompt *after* the operating rules and
 * inside a block that states its own subordinacy.
 *
 * That boundary is the reason this screen is safe to hand to a non-engineer. A prompt
 * builder that could write "answer any question the customer asks" into a position
 * where a model would obey it is a prompt builder that eventually does.
 */

interface Persona {
  name: string;
  welcomeMessage: string;
  greetingStyle: "warm" | "concise" | "formal";
  toneInstructions: string;
  starterQuestions: string[];
}

interface Refusals {
  medical: string;
  outOfScope: string;
  competitor: string;
  needsAccount: string;
  unknown: string;
  connecting: string;
  offHours: string;
}

interface AssistantConfig {
  persona: Persona;
  refusals: Refusals;
  autonomousReplies: boolean;
}

const REFUSAL_LABELS: readonly { key: keyof Refusals; label: string; hint: string }[] = [
  {
    key: "medical",
    label: "Dosing or medical questions",
    hint: "Always sent for anything about dose, administration, side effects, or a health claim.",
  },
  {
    key: "needsAccount",
    label: "Account questions from a guest",
    hint: "Shown when someone asks about orders or rewards without being signed in.",
  },
  {
    key: "unknown",
    label: "Nothing in the knowledge base answered it",
    hint: "Also used when the assistant's confidence falls below the floor.",
  },
  {
    key: "connecting",
    label: "Handing over to a person",
    hint: "Sent the moment a customer asks for a human.",
  },
  {
    key: "outOfScope",
    label: "Off topic",
    hint: "Also used for attempts to extract the prompt — deliberately the same wording, so a distinct message cannot confirm there is something to extract.",
  },
  { key: "competitor", label: "Other companies", hint: "Comparisons and recommendations." },
  { key: "offHours", label: "Outside support hours", hint: "Not yet wired to a calendar." },
];

export function PromptBuilder() {
  const { api } = useSession();

  const [config, setConfig] = useState<AssistantConfig | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [starterDraft, setStarterDraft] = useState("");

  const load = useCallback(async () => {
    try {
      setConfig(await api.get<AssistantConfig>("/admin/assistant/config"));
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(): Promise<void> {
    if (config === null) return;

    setSaving(true);
    setMessage(null);

    try {
      await api.put("/admin/assistant/config", config);
      // The AI service caches for a few seconds, so the change is live almost
      // immediately — worth saying, because an administrator who saves and sees no
      // difference will save again.
      setMessage("Saved. The assistant picks this up within a few seconds.");
    } catch {
      setMessage("That did not save. Check the fields and try again.");
    } finally {
      setSaving(false);
    }
  }

  function update(patch: Partial<Persona>): void {
    setConfig((current) =>
      current === null ? current : { ...current, persona: { ...current.persona, ...patch } },
    );
  }

  function updateRefusal(key: keyof Refusals, value: string): void {
    setConfig((current) =>
      current === null ? current : { ...current, refusals: { ...current.refusals, [key]: value } },
    );
  }

  if (status === "loading") return <p className="muted">Loading…</p>;

  if (status === "error" || config === null) {
    return (
      <p className="error">
        The assistant could not be reached. Its configuration cannot be edited right now.
      </p>
    );
  }

  return (
    <div className="admin-form">
      {/*
        How the assistant *appears*, above how it behaves. Both are the same
        decision from an administrator's point of view — what the shop's assistant
        is — and separating them across two screens meant the name customers see and
        the name in the prompt could drift apart without anybody noticing.
      */}
      <AssistantIdentity />

      <section className="card">
        <h2>How it answers</h2>

        <label>
          Name
          <input
            type="text"
            value={config.persona.name}
            maxLength={40}
            onChange={(event) => {
              update({ name: event.target.value });
            }}
          />
          <span className="hint">Shown to customers and on the typing indicator.</span>
        </label>

        <label>
          Welcome message
          <textarea
            rows={2}
            value={config.persona.welcomeMessage}
            maxLength={600}
            onChange={(event) => {
              update({ welcomeMessage: event.target.value });
            }}
          />
          <span className="hint">Shown before the customer types anything.</span>
        </label>

        <label>
          Greeting style
          <select
            value={config.persona.greetingStyle}
            onChange={(event) => {
              update({ greetingStyle: event.target.value as Persona["greetingStyle"] });
            }}
          >
            <option value="warm">Warm</option>
            <option value="concise">Concise</option>
            <option value="formal">Formal</option>
          </select>
        </label>
      </section>

      <section className="card">
        <h2>Tone</h2>

        <p className="hint">
          Describes how the assistant should sound. It is added to the prompt <strong>after</strong>{" "}
          the safety rules and cannot override them — the rules are enforced in code, before and
          after the model runs, not by the prompt.
        </p>

        <textarea
          rows={7}
          value={config.persona.toneInstructions}
          maxLength={4_000}
          placeholder="Friendly and professional. Use the customer's first name once. Keep answers short."
          onChange={(event) => {
            update({ toneInstructions: event.target.value });
          }}
        />
      </section>

      <section className="card">
        <h2>Starter questions</h2>
        <p className="hint">Offered as one-click chips before the customer types. Up to eight.</p>

        <ul className="chips">
          {config.persona.starterQuestions.map((question, index) => (
            <li key={`${question}-${String(index)}`}>
              {question}
              <button
                type="button"
                aria-label={`Remove ${question}`}
                onClick={() => {
                  update({
                    starterQuestions: config.persona.starterQuestions.filter(
                      (_, position) => position !== index,
                    ),
                  });
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>

        <div className="row">
          <input
            type="text"
            value={starterDraft}
            maxLength={160}
            placeholder="Where is my order?"
            onChange={(event) => {
              setStarterDraft(event.target.value);
            }}
          />
          <button
            type="button"
            disabled={starterDraft.trim() === "" || config.persona.starterQuestions.length >= 8}
            onClick={() => {
              update({
                starterQuestions: [...config.persona.starterQuestions, starterDraft.trim()],
              });
              setStarterDraft("");
            }}
          >
            Add
          </button>
        </div>
      </section>

      <section className="card">
        <h2>What it says when it declines</h2>
        <p className="hint">
          <strong>When</strong> the assistant declines is decided in code and cannot be changed
          here. These are the words it uses.
        </p>

        {REFUSAL_LABELS.map(({ key, label, hint }) => (
          <label key={key}>
            {label}
            <textarea
              rows={2}
              value={config.refusals[key]}
              onChange={(event) => {
                updateRefusal(key, event.target.value);
              }}
            />
            <span className="hint">{hint}</span>
          </label>
        ))}
      </section>

      <section className="card">
        <h2>Behaviour</h2>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={config.autonomousReplies}
            onChange={(event) => {
              setConfig({ ...config, autonomousReplies: event.target.checked });
            }}
          />
          <span>
            Answer customers automatically
            <span className="hint">
              Turn this off during an incident to route every conversation straight to a person. No
              deploy needed.
            </span>
          </span>
        </label>
      </section>

      <div className="row sticky-actions">
        <button className="primary" disabled={saving} onClick={() => void save()}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button disabled={saving} onClick={() => void load()}>
          Discard
        </button>
        {message !== null && <span className="small muted">{message}</span>}
      </div>
    </div>
  );
}
